import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Stack,
  Card,
  Typography,
  Button,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Container,
} from "@mui/material";
import {
  UserAdd01Icon,
  Edit04Icon,
  Delete02Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import PageHeader from "@/components/PageHeader";
import useSnackbar from "@/hooks/useSnackbar";
import {
  useFetchTeamMembersQuery,
  useFetchPendingInvitationsQuery,
  useSendInviteMutation,
  useRevokeInvitationMutation,
  useResendInviteMutation,
  useRemoveTeamMemberMutation,
  TeamMember,
  PendingInvite,
} from "@/api/accessApi";
import InviteTeamMemberDialog from "./components/InviteTeamMemberDialog";
import { stringToColor } from "@/utils/names";
import LoadingButton from "@mui/lab/LoadingButton";
import { useAppSelector } from "@/hooks";

const AccessManagementPage: React.FC = () => {
  const container = useAppSelector((state) => state.ui?.container);
  const { organizationId } = useParams<{ organizationId: string }>();
  const [openSnackbar] = useSnackbar();

  // Dialog states
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<TeamMember | null>(null);
  const [inviteToRevoke, setInviteToRevoke] = useState<PendingInvite | null>(null);

  // Pagination state for active members
  const [page, setPage] = useState(0);
  const pageSize = 5;

  // RTK Queries & Mutations
  const { data: members = [], isLoading: isLoadingMembers } =
    useFetchTeamMembersQuery(organizationId || "");
  const { data: invitations = [], isLoading: isLoadingInvites } =
    useFetchPendingInvitationsQuery(organizationId || "");


  const [sendInvite, { isLoading: isSendingInvite }] = useSendInviteMutation();
  const [revokeInvitation, { isLoading: isRevoking }] = useRevokeInvitationMutation();
  const [resendInvite, { isLoading: isResending }] = useResendInviteMutation();
  const [removeTeamMember, { isLoading: isRemoving }] = useRemoveTeamMemberMutation();

  const handleResendInvite = async (invite: PendingInvite) => {
    try {
      await resendInvite({ invitationId: invite.id }).unwrap();
      openSnackbar("Invitación reenviada con éxito", "success");
    } catch (err: any) {
      console.error(err);
      openSnackbar(err.statusText || "Error al reenviar la invitación", "error");
    }
  };

  const handleSendInviteSubmit = async (values: { email: string; role: string }) => {
    if (!organizationId) return;
    try {
      await sendInvite({
        email: values.email,
        role: values.role,
        orgId: organizationId,
      }).unwrap();
      openSnackbar("Invitación enviada con éxito", "success");
    } catch (err: any) {
      console.error(err);
      openSnackbar(err.statusText || "Error al enviar la invitación", "error");
    }
  };

  const handleRevokeConfirm = async () => {
    if (!inviteToRevoke) return;
    try {
      await revokeInvitation({ invitationId: inviteToRevoke.id }).unwrap();
      openSnackbar("Invitación cancelada con éxito", "success");
      setInviteToRevoke(null);
    } catch (err: any) {
      console.error(err);
      openSnackbar("Error al cancelar la invitación", "error");
    }
  };

  const handleRemoveMemberConfirm = async () => {
    if (!memberToRemove || !organizationId) return;
    try {
      await removeTeamMember({
        organizationId,
        userId: memberToRemove.id,
      }).unwrap();
      openSnackbar("Acceso removido con éxito", "success");
      setMemberToRemove(null);
    } catch (err: any) {
      console.error(err);
      openSnackbar("Error al remover acceso del usuario", "error");
    }
  };

  // Pagination logic
  const totalMembers = members.length;
  const paginatedMembers = members.slice(page * pageSize, (page + 1) * pageSize);
  const startIdx = totalMembers > 0 ? page * pageSize + 1 : 0;
  const endIdx = Math.min((page + 1) * pageSize, totalMembers);

  // Helper formatting functions
  const getInitials = (name: string) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  const getRoleLabel = (role: string) => {
    if (role === "admin") return "Creador"; // Custom mapping to match the design (Owner for admin role)
    if (role === "manager") return "Administrador";
    if (role === "rider") return "Mensajero";
    if (role === "super-admin") return "Super Administrador";
    return role?.charAt(0).toUpperCase() + role?.slice(1);
  };

  return (
    <Stack spacing={4} height={'100%'}>
      <PageHeader
        title="Accessos y Colaboradores"
        action={
          <Button
            size="large"
            color="success"
            startIcon={<HugeiconsIcon icon={UserAdd01Icon} size={20} color="white" />}
            onClick={() => setIsInviteOpen(true)}
            sx={{
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 500,
              paddingX: 3,
              boxShadow: "none",
            }}
          >
            Invitar Miembro
          </Button>
        }
      />
      <Stack flex={1}>
        <Container
          maxWidth={container === 'fixed' ? 'lg' : false}
          sx={{
            height: '100%',
          }}
        >
          <Stack spacing={3}>

            {/* Active Team Members Card */}
            <Card sx={{ borderRadius: 4, border: "1px solid #E2E8F0", boxShadow: "none", overflow: "hidden" }}>
              <Box px={3} py={2.5} sx={{ borderBottom: "1px solid #E2E8F0" }}>
                <Typography variant="h3">
                  Miembros del equipo
                </Typography>
              </Box>

              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
                <Table>
                  <TableHead sx={{ backgroundColor: "#F8FAFC" }}>
                    <TableRow>
                      <TableCell>
                        <Typography variant="caption">MIEMBRO</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">ROL</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">ESTADO</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">ACCIONES</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoadingMembers ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                          <CircularProgress size={30} />
                        </TableCell>
                      </TableRow>
                    ) : paginatedMembers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 6, color: "#64748B" }}>
                          No hay miembros activos en este comercio.
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedMembers.map((member) => (
                        <TableRow key={member.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                          {/* Member Details */}
                          <TableCell>
                            <Stack direction="row" spacing={2} alignItems="center">
                              <Avatar
                                sx={{
                                  bgcolor: stringToColor(member?.userName ?? ""),
                                  color: "white",
                                  fontWeight: 600,
                                  fontSize: "0.95rem",
                                  width: 42,
                                  height: 42,
                                }}
                              >
                                {getInitials(member.userName)}
                              </Avatar>
                              <Stack spacing={0.25}>
                                {member.userName}
                                <Typography variant="caption" color="textSecondary">
                                  {member.userEmail}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>

                          {/* Role */}
                          <TableCell>
                            {getRoleLabel(member.role)}
                          </TableCell>

                          {/* Status */}
                          <TableCell>
                            <Chip size="small" label="Active" color="success" variant="filled" />
                          </TableCell>

                          {/* Actions */}
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton size="small">
                                <HugeiconsIcon icon={Edit04Icon} size={18} color="#64748B" />
                              </IconButton>
                              <IconButton size="small" onClick={() => setMemberToRemove(member)}>
                                <HugeiconsIcon icon={Delete02Icon} size={18} color="#EF4444" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination Footer */}
              <Box px={3} py={2} sx={{ borderTop: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#F8FAFC" }}>
                <Typography variant="body2">
                  Mostrando {startIdx} - {endIdx} de {totalMembers} miembros
                </Typography>
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="outlined"
                    disabled={page === 0}
                    onClick={() => setPage((p) => p - 1)}
                    sx={{
                      textTransform: "none",
                      borderRadius: 2.5,
                      borderColor: "#E2E8F0",
                      color: "#475569",
                      fontWeight: 500,
                      paddingX: 2.5,
                      "&:hover": {
                        borderColor: "#CBD5E1",
                        backgroundColor: "#F1F5F9",
                      },
                    }}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={(page + 1) * pageSize >= totalMembers}
                    onClick={() => setPage((p) => p + 1)}
                    sx={{
                      textTransform: "none",
                      borderRadius: 2.5,
                      borderColor: "#E2E8F0",
                      color: "#475569",
                      fontWeight: 500,
                      paddingX: 2.5,
                      "&:hover": {
                        borderColor: "#CBD5E1",
                        backgroundColor: "#F1F5F9",
                      },
                    }}
                  >
                    Next
                  </Button>
                </Stack>
              </Box>
            </Card>

            {/* Pending Invites Card */}
            <Card sx={{ borderRadius: 4, border: "1px solid #E2E8F0", boxShadow: "none", overflow: "hidden" }}>
              <Box px={3} py={2.5} sx={{ borderBottom: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h3">
                  Invitaciones pendientes
                </Typography>
                <Chip
                  label={`${invitations.length} Activas`}
                  size="small"
                  sx={{
                    backgroundColor: "#015FEE",
                    color: "white",
                    fontWeight: 700,
                    borderRadius: 2.5,
                    fontSize: "0.75rem",
                    paddingX: 1,
                  }}
                />
              </Box>

              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
                <Table>
                  <TableHead sx={{ backgroundColor: "#F8FAFC" }}>
                    <TableRow>
                      <TableCell>
                        <Typography variant="caption">EMAIL</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">ROL</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">ESTADO</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">ACCIONES</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoadingInvites ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                          <CircularProgress size={30} />
                        </TableCell>
                      </TableRow>
                    ) : invitations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 6, color: "#64748B" }}>
                          No hay invitaciones pendientes.
                        </TableCell>
                      </TableRow>
                    ) : (
                      invitations.map((invite) => (
                        <TableRow key={invite.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                          <TableCell>
                            {invite.email}
                          </TableCell>

                          <TableCell>
                            {getRoleLabel(invite.role)}
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={invite.status === "failed" ? "Fallido" : "Enviado"}
                              size="small"
                              color={invite.status === "failed" ? "error" : "success"}
                            />
                          </TableCell>

                          <TableCell align="right">
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={isResending}
                                onClick={() => handleResendInvite(invite)}
                              >
                                Reenviar
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="error"
                                onClick={() => setInviteToRevoke(invite)}
                              >
                                Cancelar
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>

            {/* Dialogs */}
            <InviteTeamMemberDialog
              open={isInviteOpen}
              onClose={() => setIsInviteOpen(false)}
              onSubmit={handleSendInviteSubmit}
              isLoading={isSendingInvite}
            />

            {/* Remove Member Confirmation */}
            <Dialog open={Boolean(memberToRemove)} onClose={() => setMemberToRemove(null)}>
              <DialogTitle>Remover Miembro</DialogTitle>
              <DialogContent>
                <Typography>
                  ¿Estás seguro de que deseas revocar el acceso de <strong>{memberToRemove?.userName}</strong> a esta organización?
                </Typography>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setMemberToRemove(null)}>
                  Cancelar
                </Button>
                <LoadingButton
                  onClick={handleRemoveMemberConfirm}
                  variant="contained"
                  color="error"
                  loading={isRemoving}
                >
                  Remover Acceso
                </LoadingButton>
              </DialogActions>
            </Dialog>

            {/* Cancel Invitation Confirmation */}
            <Dialog open={Boolean(inviteToRevoke)} onClose={() => setInviteToRevoke(null)}>
              <DialogTitle sx={{ fontWeight: 600, color: "#07154E" }}>Cancelar Invitación</DialogTitle>
              <DialogContent>
                <Typography>
                  ¿Estás seguro de que deseas cancelar la invitación enviada a <strong>{inviteToRevoke?.email}</strong>?
                </Typography>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setInviteToRevoke(null)}
                // sx={{ color: "#64748B" }}
                >
                  Cerrar
                </Button>
                <LoadingButton
                  onClick={handleRevokeConfirm}
                  variant="contained"
                  color="error"
                  loading={isRevoking}
                  sx={{ borderRadius: 2 }}
                >
                  Cancelar Invitación
                </LoadingButton>
              </DialogActions>
            </Dialog>
          </Stack>
        </Container>
      </Stack>
    </Stack >
  );
};

export default AccessManagementPage;